import {Injectable} from '@angular/core';
import * as _ from 'lodash-es';
import {defer, EMPTY, Subject} from 'rxjs';
import {map, switchMap, switchMapTo, takeUntil, tap} from 'rxjs/operators';
import {Exchange, isExchangeAllowedToTrade} from '../../enums/exchange';
import {ExchangeAccountBaseWithLeverage} from '../../models/exchange-account';
import {formatSecurityKey, parseSecurityKey, SecurityKey} from '../../models/security-key';
import {SettingsApiService} from '../../services/settings-api.service';
import {AuthService} from '../auth/auth.service';
import {destroy$} from '../destroyable';

interface LastAccountDict {
    [key: string]: ExchangeAccountBaseWithLeverage;
}

const LAST_ACCOUNT_KEY_PARTS_SPLITTER = ':|:';

const SETTINGS_API_DEFAULT_ACCOUNT_BASE_BY_EXCHANGE_KEY = 'web-default-account-base-by-exchange';

export interface LastAccountsToSetRec {
    securityKeys: SecurityKey[];
    accountBase: ExchangeAccountBaseWithLeverage;
}

@Injectable()
export class LastAccountService {

    public readonly destroy$ = destroy$();

    private lastAccountDict: LastAccountDict = {};

    private readonly save$ = new Subject<void>();

    constructor(
        private authService: AuthService,
        private settingsApiService: SettingsApiService
    ) {
        this.initListeners();
    }

    public setLastAccount(
        securityKeys: SecurityKey[],
        accountBase: ExchangeAccountBaseWithLeverage
    ): void {
        this.innerSetLastAccount(securityKeys, accountBase);
    }

    public setLastAccounts(recs: LastAccountsToSetRec[]): void {

        // Extend recs with recs for exchange only, but only when there is no existing rec for the exchange.
        const extendedRecs = recs.reduce<LastAccountsToSetRec[]>(
            (recsAcc, rec) => {
                if (rec.securityKeys.length > 1) {
                    const lastSecurityKey: SecurityKey = _.last(rec.securityKeys);
                    const existingLastAccount = this.getLastAccount([lastSecurityKey]);

                    if (!existingLastAccount) {
                        recsAcc.push({
                            securityKeys: [lastSecurityKey],
                            accountBase:  rec.accountBase
                        });
                    }
                }

                return recsAcc;
            },
            [...recs]
        );

        const hasChanges = extendedRecs.reduce<boolean>(
            (hasChangesAcc, rec) =>
                this.innerSetLastAccount(rec.securityKeys, rec.accountBase, false)
                || hasChangesAcc,
            false
        );

        if (hasChanges) {
            this.save$.next();
        }
    }

    public getLastAccount(securityKeys: SecurityKey[]): ExchangeAccountBaseWithLeverage {
        return this.lastAccountDict[this.formatDictKey(securityKeys)];
    }

    public getLastAccountByExchange(exchange: Exchange): ExchangeAccountBaseWithLeverage {
        return this.lastAccountDict[this.formatDictKeyFromExchange(exchange)];
    }

    protected initListeners(): void {
        const auth$ = this.authService.userRole$.pipe(
            map(userRole => !!userRole)
        );

        const loading$ = defer(() =>
            this.settingsApiService.fetch<LastAccountDict>(SETTINGS_API_DEFAULT_ACCOUNT_BASE_BY_EXCHANGE_KEY).pipe(
                tap(dict => {
                    dict = this.fixDictFromSettings(dict);
                    this.lastAccountDict = dict;
                })
            )
        );

        const saving$ = this.save$.pipe(
            switchMap(() =>
                this.settingsApiService.store(
                    SETTINGS_API_DEFAULT_ACCOUNT_BASE_BY_EXCHANGE_KEY,
                    this.lastAccountDict
                )
            )
        );

        const clear$ = defer(() => {
            this.lastAccountDict = {};

            return EMPTY;
        });

        auth$.pipe(
            switchMap(isLoggedIn =>
                isLoggedIn
                    ? loading$.pipe(
                        switchMapTo(saving$)
                    )
                    : clear$
            ),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private fixDictFromSettings(dict: LastAccountDict): LastAccountDict {
        const resultDict: LastAccountDict = {};
        const isInputDictReadable = _.isPlainObject(dict);

        if (isInputDictReadable) {
            Object.keys(dict).forEach(key => {
                const securityKeys = this.parseDictKey(key);
                const isValidKey = securityKeys.every(securityKey =>
                    securityKey && isExchangeAllowedToTrade(securityKey.securityExchange)
                );

                if (isValidKey) {
                    const newKey = this.formatDictKey(securityKeys);

                    resultDict[newKey] = dict[key];
                }
            });
        }

        return resultDict;
    }

    private parseDictKey(key: string): SecurityKey[] | undefined {
        const keyParts = key.split(LAST_ACCOUNT_KEY_PARTS_SPLITTER);

        return keyParts.map(keyPart => {
            let securityKey = parseSecurityKey(keyPart);

            /**
             * Check if there is Exchange only (in {@link SecurityKey.symbol}),
             * for backward compatibility.
             */
            if (securityKey.symbol && !securityKey.securityExchange && !securityKey.currency) {
                securityKey = {
                    symbol:           null,
                    securityExchange: securityKey.symbol as Exchange,
                    currency:         null
                };
            }

            return securityKey;
        });
    }

    private formatDictKey(securityKeys: SecurityKey[]): string {
        return (securityKeys.length === 1)
            ? this.formatDictKeyFromExchange(securityKeys[0].securityExchange)
            : securityKeys
                .map(formatSecurityKey)
                .join(LAST_ACCOUNT_KEY_PARTS_SPLITTER);
    }

    private formatDictKeyFromExchange(securityExchange: Exchange): string {
        return formatSecurityKey({
            symbol:           null,
            securityExchange,
            currency:         null
        });
    }

    private innerSetLastAccount(
        securityKeys: SecurityKey[],
        accountBase: ExchangeAccountBaseWithLeverage,
        save: boolean = true
    ): boolean {
        const key = this.formatDictKey(securityKeys);
        const rec: ExchangeAccountBaseWithLeverage = {
            id:       accountBase.id,
            type:     accountBase.type,
            leverage: accountBase.leverage
        };
        const prevRec = this.lastAccountDict[key];
        const hasChanges: boolean = !_.isEqual(prevRec, rec);

        if (hasChanges) {
            this.lastAccountDict[key] = {
                id:       accountBase.id,
                type:     accountBase.type,
                leverage: accountBase.leverage
            };

            if (save) {
                this.save$.next();
            }
        }

        return hasChanges;
    }

}
